// Contenido centralizado del sistema de ayuda en pantalla.
// Cada clave corresponde a un moduleKey. Para agregar/editar la ayuda de un
// modulo solo se toca este archivo — el componente HelpButton no cambia.
//
// Estructura:
//   [moduleKey]: {
//     title: string,                       // Titulo en el panel
//     purpose: string,                     // Que hace el modulo (1-2 frases)
//     steps: string[] | { [role]: string[] }, // Pasos numerados; puede variar por rol
//     tips: string[],                      // Errores/limitaciones a evitar
//   }
//
// Roles soportados en steps (opcional): admin, dispatcher, pilot, crew_admin,
// maintenance, ccod, ccm, calidad, comercial, counter, ramp, analista, gerencia.
// Si steps es un array plano, se muestra a todos los roles.

export const helpContent = {
    // ============ APLICACION AIRLINE ============
    dashboard: {
        title: 'Dashboard',
        purpose:
            'Vista general del día operativo: vuelos programados, alertas de mantenimiento, tripulación disponible y accesos rápidos a los módulos que mas usas.',
        steps: [
            'Revisa las tarjetas de métricas para confirmar el estado del día (vuelos, aeronaves, tripulación, alertas).',
            'Usa "Vuelos de Hoy" para ver la agenda inmediata; si el número no coincide, entra a Vuelos y verifica la fecha.',
            'Los botones de "Acción Rápida" (Vuelos, Bitacora, Mantenimiento, Tripulación) te llevan directo al módulo correspondiente.',
            'Cualquier alerta crítica (documentos vencidos, diferidos vencidos) aparece en la parte superior — atiendela antes de continuar.',
        ],
        tips: [
            'Las cifras se recalculan cada vez que abres la pantalla. Si acabas de crear un vuelo y no aparece, refresca el navegador.',
            'El dashboard filtra por la fecha del sistema (UTC). Vuelos programados para "manana" en tu zona local pueden verse hoy.',
            'Si un contador dice 0 y sabes que hay datos, revisa que estes en el tenant correcto (aerolinea) en la esquina inferior izquierda.',
        ],
    },

    flights: {
        title: 'Vuelos',
        purpose:
            'Gestión completa de vuelos: crear, editar, asignar aeronave y tripulación, cambiar estado y consultar historial de cambios.',
        steps: {
            admin: [
                'Filtra por fecha o busca por número de vuelo/origen/destino usando la barra superior.',
                'Para un vuelo especial (no programado) usa el botón "Vuelo Especial".',
                'En cada vuelo puedes: asignar aeronave, asignar tripulación, cambiar estado, editar, borrar o ver historial (icono de reloj).',
                'Al asignar tripulación, el sistema válida FDTL automaticamente (Part 117 u otra regulacion configurada).',
                'Para editar un vuelo abre el vuelo → botón lapiz. Los cambios de estado se hacen desde el badge de estado, no desde editar.',
            ],
            dispatcher: [
                'Filtra los vuelos del día por fecha o estado.',
                'Para autorizar un despacho: abre el vuelo → asigna aeronave y tripulación → cambia estado a "released".',
                'Si hay diferidos vencidos el sistema NO deja autorizar; si hay diferidos abiertos no vencidos, exige confirmación explicita.',
                'Los cambios de estado (planned → active → departed → arrived) quedan registrados en la bitacora automaticamente.',
            ],
            pilot: [
                'Consulta los vuelos donde estas asignado usando el filtro de fecha.',
                'Solo puedes ver detalles y consultar; los cambios estructurales los hace despacho o admin.',
                'Registra tu bitacora manual desde el módulo Bitacora una vez que el vuelo cierra.',
            ],
            default: [
                'Filtra por fecha o busca por número de vuelo, origen o destino.',
                'Consulta el estado y detalles de cada vuelo. Los iconos de acción dependen de tu rol.',
                'El icono de reloj abre el historial de cambios (quien, cuando y que).',
            ],
        },
        tips: [
            'El campo "estado" no se puede cambiar desde "Editar Vuelo" — usa el badge de estado en la lista. Esto es a proposito para respetar permisos.',
            'Al borrar un vuelo se eliminan también sus reservations, seat_assignments, passengers, dispatch_releases y ramp_services asociados.',
            'Los vuelos nocturnos (que llegan al día siguiente) muestran un indicador "+1d". Verifica que la fecha de llegada sea la correcta.',
            'Un vuelo que ya despego (departed/arrived) crea automaticamente una entrada de bitacora. No dupliques con bitacora manual sin revisar.',
        ],
    },

    fleet: {
        title: 'Flota',
        purpose:
            'Inventario de aeronaves con matricula, tipo, base, capacidad, pesos operacionales, plantilla de asientos y horas/ciclos acumulados.',
        steps: {
            admin: [
                'Usa "Nueva Aeronave" para dar de alta. La matricula se guarda siempre en mayusculas sin espacios.',
                'Marca "Aeronave Usada" si ingresas horas/ciclos iniciales — el sistema los tomara como base histórica.',
                'Configura la plantilla de asientos: elige una preconfigurada o define columnas izq./der. y filas manualmente.',
                '"Sincronizar Estados" recalcula el bloqueo automático por diferidos/checks vencidos en toda la flota.',
                '"Recalcular Horas" reagrega horas y ciclos desde la bitacora consolidada (util si hubo edición manual).',
            ],
            maintenance: [
                'Consulta el estado operacional de cada aeronave y sus horas/ciclos actuales.',
                'Los diferidos y checks vencidos bloquean automaticamente la aeronave (estado no operacional).',
                'No edites la matricula ni datos maestros — para eso contacta al admin.',
            ],
            default: [
                'Consulta las aeronaves activas de la flota, su matricula, tipo y base.',
                'La lista muestra el estado operacional (activa, en mantenimiento, bloqueada).',
                'Solo Admin puede crear, editar o dar de baja aeronaves.',
            ],
        },
        tips: [
            'La matricula se normaliza a mayusculas al guardar. "n123ab" y "N123AB" son la misma aeronave — el sistema previene duplicados.',
            'Dar de baja una aeronave (o pasarla a almacenamiento) reduce tu total mensual: se cobra por aeronave activa al mes. Al dar de alta una, el formulario te muestra cuánto sube el total antes de guardar.',
            'Si tu flota sale del rango de tu plan (Starter 1-5, Professional 6-15, Enterprise 16+), verás un aviso: contacta a soporte para cambiar de plan. El alta no se bloquea.',
            'Las horas totales son la suma de la bitacora + horas iniciales (si la marcaste como usada). Si no cuadran, usa "Recalcular Horas".',
            'Un vuelo no puede asignarse a una aeronave bloqueada. Resuelve el diferido/check primero.',
        ],
    },

    crew: {
        title: 'Tripulación',
        purpose:
            'Gestión de pilotos y cabina: alta, licencias, médicos, base, cálculo FDTL (fatiga) en tiempo real y duty records.',
        steps: {
            admin: [
                'Pestana "Tripulantes": crea uno nuevo con "Nuevo Tripulante", asocia un usuario existente y define posicion (CAPT/FO/CA) y base.',
                'Registra vencimientos de licencia y médico — el sistema alertara cuando esten próximos a vencer.',
                'Pestana "FDTL": el sistema calcula automaticamente horas de vuelo/servicio en 24h, 7 días y 30 días según la regulacion (Part 117, OACI, etc.).',
                'Pestana "Duty Records": revisa el detalle de tarjetas de servicio; utiliza "Actualizar" tras cambios en programación.',
                '"Recalcular Horas" reprocesa las horas de todos los tripulantes desde la bitacora consolidada.',
            ],
            crew_admin: [
                'Consulta y actualiza datos de tripulantes, vencimientos y estado FDTL.',
                'Configura la regulacion FDTL vigente en el icono de engranaje (⚙️) de la pestana FDTL.',
                'Exporta el reporte FDTL a PDF cuando la autoridad lo requiera.',
            ],
            pilot: [
                'Ve tu propia ficha, tus vencimientos y tu estado FDTL personal.',
                'Si algun dato tuyo esta desactualizado (licencia, médico), avisa a admin o crew_admin.',
            ],
            default: [
                'Consulta la lista de tripulantes y sus datos de contacto y base.',
                'Los indicadores FDTL en verde/amarillo/rojo muestran quien esta apto para volar hoy.',
            ],
        },
        tips: [
            'El cálculo FDTL usa la bitacora consolidada. Si un tripulante aparece con horas raras, verifica que sus vuelos esten cerrados correctamente.',
            'Este módulo se vende por separado ("Tripulación como paquete"). Si un tenant no lo tiene habilitado, el módulo aparece bloqueado.',
            'La base se guarda en formato ICAO (4 letras, mayusculas). "SKBO", "MGGT", etc.',
            'Dar de baja un tripulante no borra su historial — lo marca como inactivo. El historial FDTL sigue disponible para auditorias.',
        ],
    },

    maintenance: {
        title: 'Mantenimiento',
        purpose:
            'Control de mantenimiento de línea (día a día) y base (checks programados), reportes de tripulación y conversion de reportes en diferidos.',
        steps: {
            admin: [
                'Pestana "Mantenimiento Línea": tareas cortas por escala. Usa "Nueva Tarea Línea" indicando aeronave, tipo, ATA, estacion y técnico.',
                'Pestana "Mantenimiento Base": ordenes de trabajo mayores. Usa "Nueva Orden Base".',
                'Pestana "Reportes": revisa los reportes de tripulación. Con "Acción" defines si se resuelve, se difiere (crea diferido/MEL) o se rechaza.',
                'Al completar una tarea, cambia el estado a "completed" y el sistema descuenta horas cuando aplique.',
            ],
            maintenance: [
                'Consulta y actualiza las tareas asignadas. "Iniciar" cambia a en progreso; "Completar" cierra la tarea.',
                'Los reportes de piloto llegan a la pestana "Reportes". Si es crítico, la acción "Diferir" crea automaticamente el registro en el módulo Diferidos.',
                'Registra siempre el número de orden de trabajo y el capitulo ATA para trazabilidad.',
            ],
            calidad: [
                'Auditas ordenes cerradas: revisa que descripcion, ATA, estacion y técnico esten completos.',
                'No modifiques tareas de otros — usa el historial de cambios (icono de reloj) para trazabilidad.',
            ],
            default: [
                'Consulta el estado de mantenimiento de la flota (línea y base).',
                'Si detectas algo, avisa al equipo de mantenimiento; solo ellos pueden crear tareas.',
            ],
        },
        tips: [
            'Este módulo se puede vender por separado. Si esta bloqueado (error 402), tu tenant no lo tiene habilitado — contacta a admin.',
            'Los reportes críticos deben pasar por "Acción → Diferir" para que aparezcan como diferido oficial y bloqueen la aeronave si corresponde.',
            'Un diferido nunca se borra permanentemente (bloqueado por auditoria). Se cierra cuando la acción correctiva esta completa.',
            'El capitulo ATA es de 2 digitos (ej. "21" para aire acondicionado, "32" para tren de aterrizaje). Es obligatorio para reportes regulatorios.',
        ],
    },

    // ============ SUPER ADMIN ============
    admin_tenants: {
        title: 'Empresas (Tenants)',
        purpose:
            'Alta y gestión de aerolineas y empresas de handling: plan, tipo, módulos vendibles y datos comerciales.',
        steps: [
            'Filtra por estado (activo/inactivo), plan o tipo (airline/ground_handler) desde la barra superior.',
            'Usa "Crear Empresa" e indica nombre, slug (URL amigable, sin espacios), país, código ICAO y tipo.',
            'Al editar un tenant, activa/desactiva "Módulos Vendibles" (Tripulación, Mantenimiento, IA, etc.) según lo que pago.',
            'El slug y el id no son editables una vez creados — se protegen automaticamente para preservar la integridad.',
            'Para cancelar el acceso: desactiva el tenant o cambia su plan; nunca borres registros historicos.',
            'En el detalle verás "Cobro mensual": aeronaves activas × precio del plan + módulos − descuento fundador. Ahí defines descuento fundador y fecha de piloto (solo superadmin; quedan en el historial).',
            'Si las aeronaves quedan fuera del rango del plan aparece el plan sugerido. El cambio de plan es manual.',
        ],
        tips: [
            'El código ICAO tiene maximo 4 letras en mayusculas (ej. "AVA" para Avianca). Se usa en integraciones externas.',
            'Los módulos vendibles se aplican al backend (require_module) y al sidebar (frontend). Si desactivas un módulo, los usuarios veran 402/oculto.',
            'La fuga de datos entre tenants esta bloqueada a nivel query — no obstante, revisa siempre estar en el tenant correcto al hacer cambios manuales.',
        ],
    },

    admin_leads: {
        title: 'Leads & CRM',
        purpose:
            'Pipeline comercial: leads capturados desde la landing y otras fuentes, con seguimiento de estado y conversion a tenant.',
        steps: [
            'Alterna entre vista "Pipeline" (kanban por estado) y "Tabla" (grilla filtrable).',
            'Usa "Nuevo Lead" para capturar empresa, contacto, email, telefono, país y fuente.',
            'Arrastra o edita el estado del lead: nuevo → contactado → propuesta → ganado/perdido.',
            'Al cerrar un lead ganado, crea el tenant desde Empresas usando los datos capturados.',
        ],
        tips: [
            'Los leads de la landing entran con estado "nuevo". Trabajalos en las primeras 24-48h — el tiempo de respuesta es el mayor predictor de cierre.',
            'No borres leads perdidos; cambia el estado a "perdido" con nota de razon. Sirve para analítica de embudo.',
            'El país se captura desde un selector en la landing; en el CRM también es libre — normaliza si vas a segmentar.',
        ],
    },

    admin_staff: {
        title: 'Equipo AeroOps',
        purpose:
            'Gestión del personal interno de AeroOps (superadmin, soporte, account manager, finanzas, developer).',
        steps: [
            'Usa "Agregar" para crear un miembro nuevo: nombre, email, rol y contraseña temporal.',
            'La contraseña debe ser fuerte — ya no hay valor por defecto conocido.',
            'Al desactivar (borrar) un miembro se le revoca el acceso en el próximo login. El registro histórico se conserva.',
            'Los roles disponibles: superadmin (todo), support (leads/tenants/actividad), account_manager (leads/tenants), billing_admin (facturacion), developer (actividad/logs).',
        ],
        tips: [
            'La cuenta con rol "superadmin" solo puede crearla otro superadmin. No compartas credenciales — cada persona debe tener la suya.',
            'Cuando desactivas a alguien, su token JWT sigue válido hasta expirar; el sistema válida is_active en cada request, así que pierde acceso de inmediato.',
            'Nunca uses contraseñas genericas tipo "aeroops123" — el sistema exige fortaleza real.',
        ],
    },

    admin_security: {
        title: 'Seguridad',
        purpose:
            'Cambio de contraseña, revision de sesiones activas y configuración de seguridad de tu propia cuenta de super admin.',
        steps: [
            'Actualiza tu contraseña periodicamente desde esta pantalla. Debe ser fuerte (minusculas, mayusculas, números y caracteres especiales).',
            'Revisa "Información de Cuenta" para confirmar tu rol y última sesion.',
            'Si sospechas actividad extrana, cambia la contraseña y avisa al equipo técnico.',
        ],
        tips: [
            'Esta pantalla solo modifica TU cuenta. Para cambiar la de otro miembro, hazlo desde Equipo AeroOps.',
            'JWT tokens del panel admin expiran automaticamente — no hay logout remoto (por ahora).',
            'No compartas capturas de tu perfil con datos de sesion; contienen información sensible.',
        ],
    },

    admin_activity: {
        title: 'Seguridad y Actividad',
        purpose:
            'Auditoria centralizada de acciones sensibles en toda la plataforma: quien hizo que, cuando y desde donde.',
        steps: [
            'Usa el filtro superior para acotar por tipo de acción (login, update_tenant, delete, etc.).',
            'Cada fila muestra usuario, tenant, acción, recurso afectado, IP y timestamp.',
            'Para trazar un incidente: filtra por usuario o acción y ordena por fecha.',
        ],
        tips: [
            'Los logs no se pueden editar ni borrar — son inmutables por diseno.',
            'Si necesitas historial detallado de un recurso (vuelo, diferido, aeronave), usa el modal de "Historial" dentro del módulo correspondiente.',
            'Las acciones críticas (delete bloqueado, cambio de rol, cambio de plan) siempre quedan aquí aunque el usuario haga rollback.',
        ],
    },

    admin_billing: {
        title: 'Facturacion (Super Admin)',
        purpose:
            'Vista global de cobros a las aerolíneas: cobro mensual esperado por aeronave (a cobrar / en piloto). La facturación es manual y se coordina directamente con cada aerolínea.',
        steps: [
            'Revisa "A cobrar / mes" y la tabla por empresa para ver plan, aeronaves facturables, módulos, descuento fundador y piloto.',
            'Registra el pago de cada mes desde la ficha de la empresa (Empresas → ficha → Pagos manuales): período, monto, estado, fecha, método, referencia y notas.',
            'Cambia el estado a Pagado o Vencido cuando corresponda; cada alta o cambio queda en el historial con usuario y fecha.',
            'Edita aquí el mensaje que ven las aerolíneas en su pantalla de Facturación.',
        ],
        tips: [
            'Esta pantalla es DIFERENTE a la de facturación de una aerolínea, que solo ve su estado de cuenta sin poder editarlo.',
            'El estado Vencido se marca a mano; no suspende el acceso automáticamente.',
        ],
    },

    admin_dashboard: {
        title: 'Dashboard (Super Admin)',
        purpose:
            'Vista ejecutiva del negocio: tenants activos, ingresos, alertas de plataforma y accesos rápidos a los módulos de gestión.',
        steps: [
            'Revisa las tarjetas de métricas: tenants activos, "A cobrar / mes" (sin pilotos), "En piloto (lista)", leads en pipeline y alertas del sistema.',
            'Los accesos rápidos te llevan a Empresas, Leads, Facturacion o Actividad según necesites.',
            'Las alertas globales (fallos de pago, tenants sin actividad, incidentes de seguridad) aparecen en la parte superior.',
        ],
        tips: [
            'Las cifras se recalculan al cargar la pantalla. Un valor "0" recien después de una acción puede requerir refresh.',
            'No confundas este dashboard con el de las aerolineas — aquí ves el negocio de AeroOps, no el de un tenant concreto.',
        ],
    },

    admin_ai: {
        title: 'Agente IA (Super Admin)',
        purpose:
            'Configuración y monitoreo de los agentes de IA a nivel plataforma: modelos usados, prompts base, uso por tenant y limites.',
        steps: [
            'Revisa el consumo por tenant para detectar abusos o tenants con volumen inusual.',
            'Configura los modelos disponibles (GPT-5.2, Claude, etc.) y los prompts base si aplica.',
            'Bloquea prompts sospechosos o palabras clave desde esta pantalla si detectas intentos de jailbreak.',
        ],
        tips: [
            'La clave de sesion de IA se genera 100% en el servidor con el patron tenant_id:user_id:agent_type — no la manipules manualmente.',
            'Los mensajes de los tenants son aislados: uno nunca puede leer conversaciones de otro tenant, incluso con la misma cuenta.',
            'El costo por token se registra por tenant para poder cobrar add-ons o facturar consumo excesivo.',
        ],
    },

    admin_support: {
        title: 'Soporte (Super Admin)',
        purpose:
            'Bandeja de tickets de soporte enviados por los tenants: gestión, respuesta y cierre.',
        steps: [
            'Filtra tickets por estado (abierto, en progreso, cerrado) o por tenant.',
            'Abre un ticket para ver el hilo completo y responde desde el mismo panel.',
            'Al cerrar el ticket, deja un comentario final que aclare la resolucion — sirve para historial.',
        ],
        tips: [
            'No borres tickets cerrados: son historial de servicio. Solo archivalos si tu flujo lo permite.',
            'Los tenants también reciben notificación de tus respuestas; ten cuidado con el tono y con datos sensibles.',
            'Si un ticket es un bug reproducible, escalalo al equipo técnico via el sistema de issues interno.',
        ],
    },

    // ============ OPERACIONES ============
    schedule: {
        title: 'Programación',
        purpose:
            'Planificación de itinerarios: crear rutinas de vuelos programados por día/semana, asignar aeronaves y horarios.',
        steps: {
            admin: [
                'Crea un vuelo programado indicando ruta (origen/destino), horarios y aeronave sugerida.',
                'Los vuelos que aterrizan al día siguiente muestran indicador "+1d" (vuelos nocturnos).',
                'Puedes duplicar un vuelo existente para generar rápidamente una rutina semanal.',
                'Los cambios en programación generan los vuelos operacionales listos para asignar tripulación y despacho.',
            ],
            comercial: [
                'Consulta la programación actual y propone cambios en horarios/rutas si el mercado lo requiere.',
                'Ver la ocupacion prevista antes de sugerir un cambio.',
            ],
            default: [
                'Consulta el itinerario semanal/diario de vuelos programados.',
                'Los cambios estructurales los hace admin o comercial.',
            ],
        },
        tips: [
            'Los horarios se guardan en la zona horaria del origen/destino de forma consistente. Verifica antes de publicar.',
            'Un vuelo con arrives_next_day=true es normal en operaciones nocturnas — no es un error.',
            'Publicar programación no crea automaticamente asignaciones de tripulación; hazlo después desde Vuelos.',
        ],
    },

    counter: {
        title: 'Mostrador',
        purpose:
            'Check-in de pasajeros, asignacion de asientos, embarque y control de vuelos en salida.',
        steps: {
            counter: [
                'Selecciona el vuelo del día para ver la lista de pasajeros.',
                'Agrega pasajeros manualmente si vienen del walk-in con los datos requeridos.',
                'Haz check-in y asigna asiento (el sistema evita duplicados por unique index de asiento).',
                'Cuando el pasajero embarca, marcalo como "boarded" — se refleja en Turnaround en tiempo real.',
            ],
            admin: [
                'Supervisa el estado de check-in y embarque de todos los vuelos del día.',
                'Puedes intervenir un mostrador si hay problemas (ej. reasignar asientos).',
            ],
            default: [
                'Consulta el estado de check-in y embarque de los vuelos del día.',
            ],
        },
        tips: [
            'La asignacion de asientos tiene protección contra condiciones de carrera (índice único Mongo). Si algo se ve extrano, refresca la pantalla.',
            'Un pasajero sin asiento no puede pasar a "boarded". Asigna primero.',
            'Si borras un pasajero, también se libera su asiento automaticamente.',
        ],
    },

    ventas: {
        title: 'Reservas (Ventas)',
        purpose:
            'Creación y gestión de reservas comerciales: pasajeros, tarifas y ligado a vuelos.',
        steps: {
            comercial: [
                'Busca el vuelo por fecha y ruta.',
                'Crea una reserva con datos del pasajero (nombre, documento, contacto) y tarifa.',
                'Al confirmar la reserva, el pasajero aparecera en Mostrador el día del vuelo listo para check-in.',
            ],
            counter: [
                'Consulta reservas existentes al hacer check-in — no necesitas crear reservas aquí salvo walk-in.',
            ],
            default: [
                'Consulta reservas del día y su estado (confirmada, cancelada, checked-in).',
            ],
        },
        tips: [
            'La tarifa (fare_usd) alimenta el módulo Revenue. Cargala siempre — sin tarifa, el vuelo no contribuye a estadisticas de ingresos.',
            'Cancelar una reserva no elimina el pasajero del historial; solo cambia su estado.',
            'Un pasajero puede tener varias reservas si compra varios tramos (multi-leg).',
        ],
    },

    dispatch: {
        title: 'Despacho de Vuelo',
        purpose:
            'Autorización operacional del vuelo: revision de diferidos, plan de vuelo, combustible y firma de despachador.',
        steps: {
            dispatcher: [
                'Selecciona el vuelo → revisa aeronave, tripulación, ruta y NOTAMs.',
                'El sistema bloquea la autorización si hay diferidos vencidos en la aeronave.',
                'Si hay diferidos abiertos NO vencidos, se exige confirmación explicita (queda registrada).',
                'Al autorizar, se genera el release y el vuelo pasa a estado "released".',
                'El cambio de estado a "departed" o "arrived" auto-crea la entrada de bitacora.',
            ],
            ccod: [
                'Supervisa releases y estados en tiempo real; puedes intervenir en caso de irregularidad.',
                'Coordina con maintenance para resolver diferidos que bloquean vuelos.',
            ],
            default: [
                'Consulta el estado de despacho de los vuelos y sus releases firmados.',
            ],
        },
        tips: [
            'Autorizar con diferidos abiertos no vencidos queda como evidencia con tu firma y timestamp. Documenta bien la decisión.',
            'Nunca cambies el estado de un vuelo saltandote la validación de diferidos — el sistema no lo permite y además queda en auditoria.',
            'Un vuelo cancelado en despacho también libera aeronave y tripulación asignadas.',
        ],
    },

    ramp: {
        title: 'Rampa',
        purpose:
            'Checklist de servicios de plataforma: chocks, GPU, combustible, catering, agua, bano, carga, limpieza y pushback.',
        steps: {
            ramp: [
                'Selecciona el vuelo asignado a tu escala.',
                'Marca cada item del checklist a medida que se completa (10 items en total).',
                'Cuando termines todos, marca "Completado" — el vuelo queda listo desde el lado de plataforma.',
                'El avance se refleja en Turnaround en tiempo real como % listo.',
            ],
            default: [
                'Consulta el estado de servicios de rampa por vuelo.',
            ],
        },
        tips: [
            'Cada item del checklist es independiente. No se puede completar el vuelo si falta algo — sirve para no olvidar nada.',
            'Si un item no aplica (ej. no hay catering), aún así marcalo (con nota si el flujo lo permite) para que Turnaround no lo cuente como pendiente.',
            'La lista de items esta hardcoded en el sistema — para agregar/quitar items contacta al equipo técnico.',
        ],
    },

    mensajes: {
        title: 'Mensajes Operacionales',
        purpose:
            'Comunicacion operacional entre tripulación, despacho, mantenimiento y mostrador vinculada a un vuelo o a un tema general.',
        steps: [
            'Selecciona el vuelo (o el canal general) para ver el hilo de mensajes.',
            'Escribe tu mensaje y envia. Los demás usuarios con acceso al vuelo lo veran en tiempo real.',
            'Usa mensajes para dejar constancia de decisiones operacionales (ej. cambio de aeronave, retraso confirmado).',
        ],
        tips: [
            'Los mensajes son parte del registro operacional — no se borran. Escribe con claridad y sin datos sensibles innecesarios.',
            'Este módulo NO reemplaza radio ni chat de aeronavegacion; es para coordinacion en tierra.',
            'Un mensaje sobre un incidente crítico deberia acompaniarse de un reporte formal en SMS.',
        ],
    },

    turnaround: {
        title: 'Turnaround',
        purpose:
            'Panel de control del tiempo de escala: seguimiento en vivo del % de listo de cada vuelo (aeronave, tripulación, despacho, rampa y pasajeros).',
        steps: {
            ccod: [
                'Ve todos los vuelos del día con su % de listo en tiempo real.',
                'Los stages son: aeronave asignada, tripulación asignada, despacho autorizado, rampa completa y pasajeros a bordo.',
                'Los vuelos ferry/charter/cargo NO cuentan pasajeros como requisito (correccion Fase A auditoria 2).',
                'Priotiza los vuelos con readiness bajo y próxima salida.',
            ],
            default: [
                'Consulta el % de listo de los vuelos donde estas involucrado.',
            ],
        },
        tips: [
            'El % de listo es una guia operativa, no un KPI oficial de puntualidad — ese sale de estadisticas.',
            'Un vuelo puede estar al 100% de listo y aún así tener alertas por retraso — mira los indicadores de tiempo.',
            'El dashboard filtra por fecha (por default hoy). Cambia el filtro para revisar días anteriores.',
        ],
    },

    logbook: {
        title: 'Bitacora',
        purpose:
            'Registro de horas de vuelo y ciclos por tripulante y por aeronave. Auto-poblado desde vuelos cerrados; permite entradas manuales cuando aplica.',
        steps: {
            pilot: [
                'Filtra por tu propio piloto para ver tu bitacora personal.',
                'Al despegar/aterrizar un vuelo, el sistema crea la entrada automaticamente — no necesitas duplicarla.',
                'Solo agrega entrada manual si el vuelo NO paso por el sistema (ej. ferry sin registro).',
                'Antes de guardar manual, el sistema verifica si ya existe una entrada automática para ese flight_id.',
            ],
            admin: [
                'Auditas la bitacora completa por tripulante o aeronave.',
                'Las entradas automáticas quedan marcadas como "auto"; las manuales como "manual" con autor.',
                'Nunca borres entradas — si hay error, agrega correccion con nota.',
            ],
            default: [
                'Consulta las horas registradas por tripulante y aeronave.',
            ],
        },
        tips: [
            'La bitacora es la fuente única para calcular FDTL y horas de aeronave. Si algo aparece raro, el problema esta aquí.',
            'Duplicados: si intentas guardar manual y ya hay una entrada automática, el sistema te avisa. Revisa antes de forzar.',
            'Las horas se guardan en formato decimal (2.5 = 2h30). No mezcles con formato HH:MM.',
        ],
    },

    // (irops se elimina del prompt: el usuario mencionó IROPS pero no existe ruta específica — se cubrirá desde el mismo módulo si aparece)

    // ============ COMERCIAL / SERVICIOS ============
    deferrals: {
        title: 'Diferidos (MEL/CDL)',
        purpose:
            'Registro y control de items diferidos (MEL/CDL): tipo, aeronave, fecha limite, estado y acción correctiva.',
        steps: {
            maintenance: [
                'Crea un diferido asociando la aeronave y el tipo (MEL, CDL, NEF).',
                'Registra la fecha limite (según manual MEL) y descripcion técnica.',
                'Al cerrar el diferido con acción correctiva, la aeronave se desbloquea automaticamente si aplica.',
            ],
            ccm: [
                'Coordina con mantenimiento para priorizar diferidos por criticidad y vuelos afectados.',
                'Consulta el historial de cambios (icono reloj) para trazabilidad.',
            ],
            dispatcher: [
                'Consulta diferidos abiertos/vencidos antes de autorizar un vuelo.',
                'Un diferido vencido bloquea la autorización; uno abierto no vencido exige confirmación explicita.',
            ],
            default: [
                'Consulta el estado de diferidos abiertos y cerrados de la flota.',
            ],
        },
        tips: [
            'Los diferidos NO se pueden borrar permanentemente. El sistema responde 409 con log de intento. Si te equivocaste, ciera con nota.',
            'Un diferido vencido bloquea el despacho de todos los vuelos con esa aeronave — resolucion prioritaria.',
            'El job de sincronizacion de flota corre cada 60s y bloquea aeronaves automaticamente al crear/vencer diferidos.',
        ],
    },

    documents: {
        title: 'Documentos',
        purpose:
            'Repositorio de documentos con vencimientos: certificados de aeronaves, licencias de tripulantes, poizas de seguros, manuales, etc.',
        steps: {
            admin: [
                'Sube un documento indicando tipo, entidad asociada (aeronave, tripulante, contrato) y fecha de vencimiento.',
                'El sistema alerta cuando un documento esta por vencer o vencio.',
                'Solo Admin, Crew Admin, Maintenance, CCM y Calidad pueden crear documentos (restricción Fase A auditoria 2).',
                'Edita o borra desde la fila del documento con los iconos de acción.',
            ],
            crew_admin: [
                'Sube certificados y licencias de tripulantes.',
                'Revisa alertas de vencimiento antes de cada rotación.',
            ],
            default: [
                'Consulta documentos vigentes y su fecha de vencimiento.',
            ],
        },
        tips: [
            'Los documentos con fecha malformada muestran advertencia pero no tumban la pantalla — se toleran por diseno.',
            'Si borras un documento y era el único vigente, la entidad asociada puede quedar sin cobertura — revisa antes.',
            'No subas archivos con datos personales de terceros sin autorización (LOPD/GDPR).',
        ],
    },

    sms: {
        title: 'SMS Seguridad',
        purpose:
            'Sistema de Gestión de Seguridad Operacional: reporte de eventos, investigacion, acciones correctivas y seguimiento.',
        steps: {
            admin: [
                'Todos los usuarios pueden reportar un evento (voluntario o obligatorio).',
                'Los eventos entran con estado "reportado" y son evaluados por Admin/CCOD/Gerencia/Calidad.',
                'Agregar "Acción Correctiva" esta restringido a roles de investigacion (Admin/CCOD/Gerencia/Calidad) — cambio Fase A auditoria 2.',
                'Cierra el evento con conclusion y acciones tomadas.',
            ],
            calidad: [
                'Investigas eventos críticos y defines acciones correctivas.',
                'Analítica de tendencias por tipo de evento y por trimestre.',
            ],
            pilot: [
                'Puedes reportar eventos que observes. Se mantiene confidencialidad conforme al SMS.',
                'No puedes agregar acción correctiva — es responsabilidad de investigacion.',
            ],
            default: [
                'Puedes reportar eventos de seguridad. Las acciones correctivas las agrega el equipo de investigacion.',
            ],
        },
        tips: [
            'El reporte confidencial NO castiga al reportante (cultura justa). Foco en aprender del error, no en sancionar.',
            'Un evento sin acción correctiva queda abierto — es responsabilidad del equipo de investigacion cerrarlo.',
            'Los datos del SMS son sensibles: no compartas fuera de la plataforma sin autorización del CCOD.',
        ],
    },

    // ============ ANALITICA / OTROS ============
    reports: {
        title: 'Reportes',
        purpose:
            'Reportes operacionales exportables: puntualidad, utilizacion de aeronaves, horas de tripulación, FDTL, etc.',
        steps: [
            'Selecciona el tipo de reporte y el rango de fechas.',
            'Aplica filtros adicionales (aeronave, tripulante, ruta) si el reporte lo permite.',
            'Exporta a PDF o CSV según tu necesidad.',
        ],
        tips: [
            'Los reportes se calculan on-demand — un rango muy grande puede tardar. Empieza con rangos cortos.',
            'Si el reporte incluye cifras estimadas, veras la etiqueta "is_estimated" — no la uses para reporteo oficial sin validar.',
            'Los reportes exportados quedan con fecha/hora de generación — util para trazabilidad.',
        ],
    },

    statistics: {
        title: 'Estadisticas',
        purpose:
            'KPIs y gráficas operacionales en tiempo real: vuelos por día, ocupacion, puntualidad, horas de flota y tripulación.',
        steps: [
            'Filtra por rango de fechas para ajustar el período.',
            'Las tarjetas superiores resumen los KPIs clave; las gráficas muestran tendencias.',
            'Usa las tabs para segmentar por dimension (flota, tripulación, rutas).',
        ],
        tips: [
            'Las cifras vienen del backend con agregaciones MongoDB ($lookup) — son consistentes con los otros módulos.',
            'Si un KPI se ve raro, verifica en el módulo fuente (Vuelos, Bitacora) que los datos esten completos.',
            'La ocupacion se calcula con pasajeros checked-in / capacidad de aeronave configurada en Flota.',
        ],
    },

    cco: {
        title: 'Centro de Control',
        purpose:
            'Dashboard operacional en vivo (Control Center Operations): estado global de la flota, alertas activas, vuelos críticos.',
        steps: [
            'Vista consolidada del día: vuelos en operación, listos, retrasados, cancelados.',
            'Alertas ordenadas por criticidad; atiende primero las rojas.',
            'Las acciones rápidas te llevan al módulo correspondiente sin perder contexto.',
        ],
        tips: [
            'Este es el "puente de mando". Manten esta pantalla abierta si eres CCOD durante turnos operacionales.',
            'La información se refresca sola en ciclos cortos — pero si algo se ve congelado, refresca el navegador.',
            'Las decisiones que tomes desde aquí quedan registradas en Actividad del super admin — trazabilidad total.',
        ],
    },

    crew_calendar: {
        title: 'Calendario Crew',
        purpose:
            'Vista de calendario con asignaciones, servicios, días libres y vacaciones de la tripulación.',
        steps: {
            crew_admin: [
                'Selecciona el mes/semana y filtra por tripulante o base.',
                'Cada día muestra las asignaciones y su estado (asignado, libre, vacaciones, off).',
                'Detectas conflictos visualmente antes de que sean problemas operativos.',
            ],
            pilot: [
                'Consulta tu propio calendario para planear tus días.',
                'Solicita cambios via Crew Admin — no se editan desde aquí directamente.',
            ],
            default: [
                'Consulta la planificación de crew para vuelos en los que estas involucrado.',
            ],
        },
        tips: [
            'El calendario se alimenta de asignaciones en Vuelos + duty records. Si algo falta, verifica en Vuelos primero.',
            'Los días libres se calculan automaticamente según FDTL — no los edites manualmente sin criterio.',
            'Un tripulante puede aparecer en gris si esta inactivo — no puede recibir asignaciones nuevas.',
        ],
    },

    billing: {
        title: 'Facturacion (Aerolinea)',
        purpose:
            'Plan y cobro de tu aerolinea: se paga por aeronave activa al mes (Starter USD 390 de 1 a 5 aeronaves, Professional USD 320 de 6 a 15, Enterprise USD 260 desde 16) más los módulos contratados con precio fijo mensual.',
        steps: {
            admin: [
                'Consulta el desglose "N aeronaves × precio", los módulos contratados y el total mensual.',
                'Pago anual: 2 meses sin costo (pagas 10 meses).',
                'Si tu flota sale del rango de tu plan verás un aviso: contacta a soporte para cambiar de plan.',
                'Durante el periodo de prueba verás "Periodo de prueba hasta [fecha]" y no se cobra.',
            ],
            gerencia: [
                'Consulta el gasto mensual en la plataforma y el historial de pagos.',
                'Los cambios de plan los gestiona soporte de AeroOps a pedido del Admin.',
            ],
            default: [
                'Consulta el plan actual y el total mensual.',
            ],
        },
        tips: [
            'Aeronaves facturables: incluye todas las aeronaves salvo las almacenadas. Si tienes precio fundador, verás la línea "Precio fundador −X% hasta [fecha]" y el total real a pagar.',
            'Módulos: Tripulaciones USD 79, Mantenimiento USD 99, Mtto. Predictivo USD 89, Revenue & Yield USD 59, Agentes IA USD 119 (al mes).',
            'Los módulos vendibles se activan/desactivan desde super admin, no desde aquí.',
        ],
    },

    revenue: {
        title: 'Revenue & Yield',
        purpose:
            'Analítica de ingresos por vuelo, ruta y período: yield, load factor, RASK, CASK y proyecciones.',
        steps: [
            'Filtra por rango de fechas y ruta para obtener métricas segmentadas.',
            'Las cifras "reales" vienen de reservations con tarifa (fare_usd); las estimadas se marcan con "is_estimated": true (Fase A auditoria 2).',
            'Usa Seed Revenue SOLO en ambiente demo/dev — el endpoint esta bloqueado en produccion.',
        ],
        tips: [
            'Etiqueta "estimado": estas cifras se calculan a partir de defaults cuando no hay tarifa real cargada. No usar para reporteo oficial.',
            'Yield y RASK dependen de que Reservas tengan fare_usd. Sin tarifa, esa fila no aporta al cálculo.',
            'El bloqueo del seed en produccion depende de la variable APP_ENV — no la modifiques en runtime.',
        ],
    },

    ai_agents: {
        title: 'Agentes IA',
        purpose:
            'Chat con agentes especializados de IA (dispatcher, mantenimiento, crew, general) que consultan datos de tu tenant para ayudarte a decidir.',
        steps: [
            'Selecciona el agente según tu necesidad (ej. "Dispatch Agent" para planificación, "Maintenance Agent" para diferidos).',
            'Escribe tu pregunta en lenguaje natural. El agente responde con datos de tu tenant.',
            'La sesion se mantiene por rol y tipo de agente — puedes retomar conversaciones anteriores.',
        ],
        tips: [
            'La IA nunca ve datos de otros tenants: el session_id se genera 100% en el servidor a partir de tu tenant_id + user_id + tipo de agente.',
            'No pegues prompts que intenten "romper" al agente — hay un bloqueo activo contra prompt injection.',
            'La respuesta de la IA es una recomendacion, no una decisión automática. Válida siempre con procedimientos operacionales.',
        ],
    },

    predictive_mx: {
        title: 'Mantenimiento Predictivo',
        purpose:
            'Prediccion de fallas y necesidades de mantenimiento basadas en horas/ciclos, reportes recurrentes y patrones historicos.',
        steps: [
            'Consulta los items destacados: aeronaves con checks próximos, patrones de reportes recurrentes.',
            'Programa mantenimiento preventivo antes de que un item se convierta en diferido crítico.',
            'La lista se actualiza según horas voladas y datos de la bitacora consolidada.',
        ],
        tips: [
            'Es una herramienta de apoyo, no un sistema aprobado por autoridad. Las decisiones formales siguen el programa oficial de mantenimiento (AMP).',
            'Si una aeronave no aparece con predicciones, es porque tiene pocos datos historicos aún.',
            'Coordina con Base Maintenance para incorporar sugerencias al plan mensual.',
        ],
    },

    // ============ GROUND HANDLING ============
    gh_dashboard: {
        title: 'Dashboard Ground Handling',
        purpose:
            'Vista de operaciones de handling: turnarounds asignados, equipos, aerolineas cliente y KPIs de servicio.',
        steps: {
            gh_supervisor: [
                'Ve todos los turnarounds del día con su estado en tiempo real.',
                'Asigna equipos a turnarounds nuevos.',
                'Los KPIs muestran ontime performance y cumplimiento SLA.',
            ],
            gh_leader: [
                'Consulta los turnarounds asignados a tu equipo.',
                'Actualiza el estado de cada tarea de handling en tiempo real.',
            ],
            default: [
                'Consulta la operación de handling del día.',
            ],
        },
        tips: [
            'El módulo GH esta en Fase 1 — proximamente se agregara gestión detallada de equipos y reportes SLA.',
            'Las métricas de puntualidad se calculan sobre turnarounds cerrados con hora real registrada.',
            'Los tenants tipo "ground_handler" tienen esta interfaz separada de las aerolineas.',
        ],
    },

    // ============ OTROS ============
    support: {
        title: 'Soporte',
        purpose:
            'Envio de tickets al equipo de AeroOps: bugs, dudas o solicitudes de nuevas funcionalidades.',
        steps: [
            'Crea un ticket describiendo el problema o la solicitud con el mayor detalle posible (pasos, capturas si aplica).',
            'Selecciona la categoría (bug, mejora, duda, urgente).',
            'Consulta el estado de tickets anteriores y las respuestas del equipo.',
        ],
        tips: [
            'Un bug reproducible con pasos claros se resuelve mucho mas rápido. Adjunta screenshots si puedes.',
            'No compartas contraseñas ni tokens en tickets — nunca son necesarios para reproducir un bug.',
            'Los tickets críticos operacionales (vuelo bloqueado, sistema caido) deben marcarse como urgentes.',
        ],
    },

    users: {
        title: 'Mi Equipo',
        purpose:
            'Alta y gestión de usuarios internos de la aerolinea (pilotos, despachadores, mantenimiento, mostrador, etc.).',
        steps: {
            admin: [
                'Usa "Nuevo Usuario" para crear una cuenta con email, nombre, rol y contraseña inicial.',
                'La contraseña debe ser fuerte — el sistema ya no permite valor por defecto (Fase 1 auditoria 1).',
                'Al desactivar un usuario, pierde acceso en el próximo request (login válida is_active).',
                'Un usuario con rol "pilot" o "crew" se refleja también en el módulo Tripulación al asociarlo.',
            ],
            default: [
                'Solo Admin puede gestionar usuarios de la aerolinea. Contacta a tu admin si necesitas cuentas nuevas.',
            ],
        },
        tips: [
            'Cada persona debe tener su propia cuenta. No comparta credenciales — cada acción queda en auditoria con el user_id.',
            'Si desactivas a alguien por seguridad, cambialo antes de que expire su token. El sistema revoca acceso en la siguiente peticion.',
            'Los roles disponibles se definen a nivel plataforma; si necesitas uno nuevo, abre ticket en Soporte.',
        ],
    },
};

/**
 * Resuelve el contenido de ayuda para un moduleKey dado.
 * Si el modulo tiene steps por rol, retorna los steps del rol o los default.
 */
export const getHelpForModule = (moduleKey, userRole = 'default') => {
    const entry = helpContent[moduleKey];
    if (!entry) return null;

    let steps = entry.steps;
    if (steps && !Array.isArray(steps) && typeof steps === 'object') {
        steps = steps[userRole] || steps.default || Object.values(steps)[0] || [];
    }
    return {
        title: entry.title,
        purpose: entry.purpose,
        steps,
        tips: entry.tips || [],
    };
};

/**
 * Mapa de rutas → moduleKey. Se resuelve por prefijo (startsWith).
 * El orden importa: rutas mas especificas primero.
 */
export const routeToModuleKey = [
    // Admin (mas especifico primero)
    { prefix: '/admin/tenants', moduleKey: 'admin_tenants' },
    { prefix: '/admin/leads', moduleKey: 'admin_leads' },
    { prefix: '/admin/billing', moduleKey: 'admin_billing' },
    { prefix: '/admin/staff', moduleKey: 'admin_staff' },
    { prefix: '/admin/activity', moduleKey: 'admin_activity' },
    { prefix: '/admin/security', moduleKey: 'admin_security' },
    { prefix: '/admin/support', moduleKey: 'admin_support' },
    { prefix: '/admin/ai', moduleKey: 'admin_ai' },
    { prefix: '/admin/dashboard', moduleKey: 'admin_dashboard' },
    // Airline (rutas mas especificas primero)
    { prefix: '/crew-calendar', moduleKey: 'crew_calendar' },
    { prefix: '/predictive-mx', moduleKey: 'predictive_mx' },
    { prefix: '/ai-agents', moduleKey: 'ai_agents' },
    { prefix: '/dashboard', moduleKey: 'dashboard' },
    { prefix: '/schedule', moduleKey: 'schedule' },
    { prefix: '/flights', moduleKey: 'flights' },
    { prefix: '/fleet', moduleKey: 'fleet' },
    { prefix: '/crew', moduleKey: 'crew' },
    { prefix: '/counter', moduleKey: 'counter' },
    { prefix: '/ventas', moduleKey: 'ventas' },
    { prefix: '/dispatch', moduleKey: 'dispatch' },
    { prefix: '/ramp', moduleKey: 'ramp' },
    { prefix: '/mensajes', moduleKey: 'mensajes' },
    { prefix: '/turnaround', moduleKey: 'turnaround' },
    { prefix: '/maintenance', moduleKey: 'maintenance' },
    { prefix: '/deferrals', moduleKey: 'deferrals' },
    { prefix: '/logbook', moduleKey: 'logbook' },
    { prefix: '/statistics', moduleKey: 'statistics' },
    { prefix: '/cco', moduleKey: 'cco' },
    { prefix: '/documents', moduleKey: 'documents' },
    { prefix: '/sms', moduleKey: 'sms' },
    { prefix: '/reports', moduleKey: 'reports' },
    { prefix: '/billing', moduleKey: 'billing' },
    { prefix: '/revenue', moduleKey: 'revenue' },
    { prefix: '/gh', moduleKey: 'gh_dashboard' },
    { prefix: '/support', moduleKey: 'support' },
    { prefix: '/users', moduleKey: 'users' },
];

export const getModuleKeyFromPath = (pathname) => {
    if (!pathname) return null;
    const match = routeToModuleKey.find((r) => pathname.startsWith(r.prefix));
    return match ? match.moduleKey : null;
};
